import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LoginResult, MfaType, selectMfaType } from './api';
import { useFormSubmit } from './useFormSubmit';

interface Props {
  session: string;
  email: string;
  options: MfaType[];
  /** Called with the challenge Cognito returns for the chosen method. */
  onChallenge: (challenge: Extract<LoginResult, { status: 'MFA_REQUIRED' }> & { email: string }) => void;
  onBack: () => void;
}

const LABEL_KEYS: Record<MfaType, string> = {
  SMS_MFA: 'login.selectMfa.sms',
  SOFTWARE_TOKEN_MFA: 'login.selectMfa.totp',
};

/**
 * Answers a SELECT_MFA_TYPE challenge, raised when an account has both SMS and an
 * authenticator enrolled and neither is preferred.
 *
 * Choosing does not sign the user in — Cognito replies with the chosen method's own
 * challenge, which the existing MfaForm then answers.
 */
export const MfaTypePicker = ({ session, email, options, onChallenge, onBack }: Props) => {
  const { t } = useTranslation();
  const { loading, error, setError, runSubmit } = useFormSubmit();

  const choose = (mfaType: MfaType) =>
    runSubmit(async () => {
      const result = await selectMfaType({ session, email, mfaType });
      if (result.status === 'MFA_REQUIRED') {
        onChallenge({ ...result, email });
      }
    });

  return (
    <Box>
      <Stack spacing={2.5}>
        <Typography variant="body2" color="text.secondary">
          {t('login.selectMfa.description')}
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {options.map((option) => (
          <Button
            key={option}
            variant="outlined"
            size="large"
            fullWidth
            disabled={loading}
            onClick={() => choose(option)}
            sx={{ height: '3rem' }}
          >
            {t(LABEL_KEYS[option])}
          </Button>
        ))}

        <Button variant="text" size="small" onClick={onBack} sx={{ alignSelf: 'center' }}>
          {t('action.backToLogin')}
        </Button>
      </Stack>
    </Box>
  );
};
